import { Controller } from "@hotwired/stimulus"
import { logger } from "../utilities/logger"

// Handles FAB button interactions and modal positioning
// Makes modals appear to originate from the FAB button position
export default class extends Controller {
  static values = {
    open: { type: Boolean, default: false }
  }

  connect() {
    logger.log("FAB button controller connected (Material Design Speed Dial)")

    // Store FAB button position when menu items are clicked
    this.setupMenuClickTracking()

    // Position modals to originate from FAB when they open
    this.setupModalPositioning()
  }

  toggle(event) {
    event?.preventDefault()
    event?.stopPropagation()

    if (this.openValue) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.openValue = true
    this.element.classList.add('fab-open')
    logger.log('FAB menu opened')
  }

  close() {
    this.openValue = false
    this.element.classList.remove('fab-open')
    logger.log('FAB menu closed')
  }

  setupMenuClickTracking() {
    // Find all menu item links within this FAB
    const menuItems = this.element.querySelectorAll('.fab-menu-item a')

    menuItems.forEach(item => {
      item.addEventListener('click', (e) => {
        // Get FAB button position
        const fabButton = this.element.querySelector('.fab-button')
        if (fabButton) {
          const rect = fabButton.getBoundingClientRect()
          const fabPosition = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
            width: rect.width,
            height: rect.height
          }

          // Store in sessionStorage so modal can access it
          sessionStorage.setItem('fabOriginPosition', JSON.stringify(fabPosition))
          logger.log('FAB position stored:', fabPosition)
        }
      })
    })
  }

  setupModalPositioning() {
    // Listen for modals being inserted into the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if it's a modal or contains a modal
            const modal = node.classList?.contains('modal')
              ? node
              : node.querySelector?.('.modal')

            if (modal) {
              // Position modal to originate from FAB
              this.positionModalFromFab(modal)
            }
          }
        })
      })
    })

    // Observe the modals container
    const modalsContainer = document.getElementById('modals')
    if (modalsContainer) {
      observer.observe(modalsContainer, { childList: true, subtree: true })
    }

    // Also check for modals in the main content (for Turbo Frame navigation)
    const mainContent = document.querySelector('turbo-frame#main_content')
    if (mainContent) {
      observer.observe(mainContent, { childList: true, subtree: true })
    }
  }

  positionModalFromFab(modal) {
    // Get stored FAB position
    const fabPositionJson = sessionStorage.getItem('fabOriginPosition')
    if (!fabPositionJson) {
      return // No FAB position stored, use default positioning
    }

    try {
      const fabPosition = JSON.parse(fabPositionJson)

      // Wait for modal to be inserted and measured
      requestAnimationFrame(() => {
        // Get modal dialog element
        const modalDialog = modal.querySelector('.modal-dialog') || modal

        if (!modalDialog) {
          return
        }

        // Calculate transform origin relative to viewport
        // Bootstrap modals are centered, so we need to calculate the origin point
        const viewportCenterX = window.innerWidth / 2
        const viewportCenterY = window.innerHeight / 2

        // Calculate offset from viewport center to FAB position
        const offsetX = fabPosition.x - viewportCenterX
        const offsetY = fabPosition.y - viewportCenterY

        // Set transform origin to appear from FAB position
        // Use percentage-based origin for better cross-screen compatibility
        const originX = 50 + (offsetX / modalDialog.offsetWidth) * 100
        const originY = 50 + (offsetY / modalDialog.offsetHeight) * 100

        modalDialog.style.transformOrigin = `${Math.max(0, Math.min(100, originX))}% ${Math.max(0, Math.min(100, originY))}%`
        modalDialog.style.transform = 'scale(0.1)'
        modalDialog.style.opacity = '0'
        modalDialog.style.transition = 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease'

        // Animate to final position
        requestAnimationFrame(() => {
          modalDialog.style.transform = 'scale(1)'
          modalDialog.style.opacity = '1'
        })

        // Clean up stored position after animation completes
        setTimeout(() => {
          sessionStorage.removeItem('fabOriginPosition')
        }, 500)

        logger.log('Modal positioned from FAB:', fabPosition)
      })
    } catch (e) {
      logger.error('Error positioning modal from FAB:', e)
      sessionStorage.removeItem('fabOriginPosition')
    }
  }
}
